import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MessageHtmlEditor } from './MessageHtmlEditor';
import type { MessageHtmlEditorHandle } from './MessageHtmlEditorHandle';
import { MessageMacroChips } from './MessageMacroChips';
import { ActiveStatusBadge } from '../ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../ui/CollapsibleFormSection';
import { FormActionBar } from '../ui/FormActionBar';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { EditIconButton } from '../ui/EditIconButton';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { RequiredMark } from '../ui/RequiredMark';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  EmailMacro,
  EmailTemplate,
  EmailTemplateUpsert,
  createEmailTemplate,
  deleteEmailTemplate,
  listEmailMacros,
  listEmailTemplates,
  updateEmailTemplate,
} from '../../services/emailTemplatesApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

export const EMAIL_TEMPLATE_KINDS = [
  { value: 'Welcome', label: 'Boas-vindas' },
  { value: 'Onboarding', label: 'Onboarding' },
  { value: 'Update', label: 'Update' },
  { value: 'Notice', label: 'Aviso' },
  { value: 'Csat', label: 'CSAT' },
  { value: 'Nps', label: 'NPS' },
  { value: 'Custom', label: 'Customizada' },
];

export function emailTemplateKindLabel(kind: string): string {
  return EMAIL_TEMPLATE_KINDS.find((item) => item.value === kind)?.label ?? kind;
}

type ModuleOption = {
  code: string;
  name: string;
};

type EmailTemplatesCrudSectionProps = {
  scope: 'admin' | 'customer-success';
  canWrite: boolean;
  moduleOptions?: ModuleOption[];
  fixedModuleCodes?: string[];
  showModulePicker?: boolean;
  createButtonLabel?: string;
};

const emptyForm = (moduleCodes: string[]): EmailTemplateUpsert => ({
  name: '',
  kind: 'Custom',
  subject: '',
  bodyHtml: '<p>Olá {{contactFirstName}},</p>\n<p></p>',
  bodyText: '',
  isActive: true,
  moduleCodes,
});

export function EmailTemplatesCrudSection({
  scope,
  canWrite,
  moduleOptions = [],
  fixedModuleCodes,
  showModulePicker = false,
  createButtonLabel = 'Novo template',
}: EmailTemplatesCrudSectionProps) {
  const { confirm } = useConfirmDialog();
  const defaultModules = fixedModuleCodes?.length
    ? fixedModuleCodes
    : scope === 'customer-success'
      ? ['customer-success.engagement']
      : [];
  const [items, setItems] = useState<EmailTemplate[]>([]);
  const [macros, setMacros] = useState<EmailMacro[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EmailTemplateUpsert>(() => emptyForm(defaultModules));
  const [saving, setSaving] = useState(false);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const messageEditorRef = useRef<MessageHtmlEditorHandle>(null);
  const macroTargetRef = useRef<'subject' | 'message'>('subject');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [list, macroList] = await Promise.all([
        listEmailTemplates(scope),
        listEmailMacros(scope),
      ]);
      setItems(list);
      setMacros(macroList);
    } catch {
      toast.error('Falha ao carregar templates de e-mail.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  function insertMacroToken(token: string): void {
    if (macroTargetRef.current === 'subject') {
      const input = subjectInputRef.current;
      if (!input) {
        setForm((current) => ({ ...current, subject: `${current.subject}${token}` }));
        return;
      }

      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const next = `${input.value.slice(0, start)}${token}${input.value.slice(end)}`;
      setForm((current) => ({ ...current, subject: next }));
      requestAnimationFrame(() => {
        input.focus();
        const caret = start + token.length;
        input.setSelectionRange(caret, caret);
      });
      return;
    }

    messageEditorRef.current?.insertText(token);
  }

  function startCreate(): void {
    setEditingId(null);
    setForm(emptyForm(defaultModules));
    expandForm();
  }

  function startEdit(item: EmailTemplate): void {
    setEditingId(item.id);
    setForm({
      name: item.name,
      kind: item.kind,
      subject: item.subject,
      bodyHtml: item.bodyHtml,
      bodyText: item.bodyText ?? '',
      isActive: item.isActive,
      moduleCodes: item.moduleCodes?.length ? [...item.moduleCodes] : [...defaultModules],
    });
    expandForm();
  }

  function toggleModuleCode(code: string): void {
    setForm((current) => {
      const exists = current.moduleCodes.includes(code);
      return {
        ...current,
        moduleCodes: exists
          ? current.moduleCodes.filter((item) => item !== code)
          : [...current.moduleCodes, code],
      };
    });
  }

  function resolveModuleLabels(codes: string[] | undefined): string {
    if (!codes?.length) {
      return '—';
    }

    return codes
      .map((code) => moduleOptions.find((module) => module.code === code)?.name ?? code)
      .join(', ');
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form.name.trim() || !form.subject.trim() || !form.bodyHtml.trim()) return;
    if (showModulePicker && form.moduleCodes.length === 0) {
      toast.info('Selecione ao menos um módulo consumidor.');
      return;
    }

    setSaving(true);
    try {
      const payload: EmailTemplateUpsert = {
        ...form,
        name: form.name.trim(),
        subject: form.subject.trim(),
        bodyHtml: form.bodyHtml.trim(),
        bodyText: form.bodyText?.trim() || null,
        moduleCodes: form.moduleCodes.length ? form.moduleCodes : defaultModules,
      };
      if (editingId) {
        await updateEmailTemplate(editingId, payload, scope);
        toast.success('Template atualizado.');
        collapseForm(() => {
          setEditingId(null);
          setForm(emptyForm(defaultModules));
        });
      } else {
        await createEmailTemplate(payload, scope);
        toast.success('Template criado.');
        handleCreateSuccess(() => {
          setEditingId(null);
          setForm(emptyForm(defaultModules));
        });
      }
      setEditingId(null);
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar template.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: EmailTemplate): Promise<void> {
    if (!canWrite) return;
    const ok = await confirm({
      title: 'Remover template',
      message: `Remover "${item.name}"?`,
    });
    if (!ok) return;
    try {
      await deleteEmailTemplate(item.id, scope);
      toast.success('Template removido.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao remover.'));
    }
  }

  return (
    <div className="space-y-4">
      {canWrite ? (
        <div className="flex justify-end">
          <button type="button" className="erp-btn-primary" onClick={startCreate}>
            {createButtonLabel}
          </button>
        </div>
      ) : null}

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar template' : 'Novo template'}
          expanded={formExpanded}
          onToggle={() =>
            toggleOrCancel(() => {
              setEditingId(null);
              setForm(emptyForm(defaultModules));
            })
          }
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form-grid">
            <label className="erp-label">
              Nome <RequiredMark />
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="erp-label">
              Tipo
              <select
                className="erp-input"
                value={form.kind}
                onChange={(event) => setForm({ ...form, kind: event.target.value })}
              >
                {EMAIL_TEMPLATE_KINDS.map((kind) => (
                  <option key={kind.value} value={kind.value}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </label>
            {showModulePicker ? (
              <div className="erp-label erp-form-grid-full">
                <span>
                  Módulos consumidores <RequiredMark />
                </span>
                <div className="mt-2 flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-lg border border-secondary-200 p-2">
                  {moduleOptions.length === 0 ? (
                    <p className="text-xs text-secondary-500">Nenhum módulo disponível.</p>
                  ) : (
                    moduleOptions.map((module) => {
                      const checked = form.moduleCodes.includes(module.code);
                      return (
                        <label
                          key={module.code}
                          className="inline-flex items-center gap-1.5 rounded-md border border-secondary-200 bg-white px-2 py-1 text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleModuleCode(module.code)}
                          />
                          <span title={module.code}>{module.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            ) : null}
            <MessageMacroChips macros={macros} disabled={saving} onInsert={insertMacroToken} />
            <label className="erp-label erp-form-grid-full">
              Assunto <RequiredMark />
              <input
                ref={subjectInputRef}
                className="erp-input"
                value={form.subject}
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
                onFocus={() => {
                  macroTargetRef.current = 'subject';
                }}
                required
                placeholder="Bem-vindo, {{companyName}}"
              />
            </label>
            <MessageHtmlEditor
              ref={messageEditorRef}
              value={form.bodyHtml}
              onChange={(bodyHtml) => setForm({ ...form, bodyHtml })}
              disabled={saving}
              onFocusField={() => {
                macroTargetRef.current = 'message';
              }}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              />
              Ativo
            </label>
            <FormActionBar
              className="erp-btn-actions erp-form-grid-full"
              onCancel={() =>
                toggleOrCancel(() => {
                  setEditingId(null);
                  setForm(emptyForm(defaultModules));
                })
              }
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhum template cadastrado.</p>
      ) : (
        <div className="erp-table-wrap">
          <div className="erp-table-scroll">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  {showModulePicker ? <th>Módulos</th> : null}
                  <th className="w-32">Status</th>
                  {canWrite ? <th className="w-24" /> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="font-medium text-secondary-900">{item.name}</td>
                    <td>{emailTemplateKindLabel(item.kind)}</td>
                    {showModulePicker ? (
                      <td className="text-xs text-secondary-600" title={item.moduleCodes?.join(', ')}>
                        {resolveModuleLabels(item.moduleCodes)}
                      </td>
                    ) : null}
                    <td>
                      <ActiveStatusBadge active={item.isActive} />
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="flex justify-end gap-1">
                          <EditIconButton onClick={() => startEdit(item)} />
                          <DeleteIconButton onClick={() => void handleDelete(item)} />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
